import React, { FC, ReactNode, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, RADII, SIZES, SPACINGS } from "../../common/consts";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Text from "../Display/Text";

interface ToggleContainerProps {
  saving?: boolean;
}

const shouldForwardProp = makeShouldForwardProps<ToggleContainerProps>([
  "saving",
]);

const ToggleContainer = styled("div", {
  shouldForwardProp,
})<ToggleContainerProps>`
  display: flex;
  align-items: center;
  margin-left: -${SPACINGS.sm};
`;

const ToggleInput = styled("input")`
  display: none;
`;

const Slider = styled("span")<{ active?: boolean; saving?: boolean; disabled?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${SIZES.toggleWidth};
  height: ${SIZES.toggleHeight};
  background-color: ${({ active, saving, disabled }) => {
    if (saving) return "var(--color-saveButtonDisabledIcon, #DBDAE3)";
    if (disabled) return COLORS.toggleInactive;
    if (active) return COLORS.toggleActive;
    return COLORS.toggleInactive;
  }};
  border-radius: ${RADII.lg};
  z-index: 1;
  transition: ${({ saving }) => (saving ? "none" : "background-color 0.15s ease, box-shadow 0.15s ease")};
  box-shadow: none;
  opacity: ${({ disabled }) => (disabled ? 1 : 1)};

  /* HubSpot's toggle (no capture: built from its tokens): a 36x20 pill,
     #00494B on, #CCC off. */
  html[data-crm="hubspot"] & {
    width: 36px;
    height: 20px;
    border-radius: 999px;
    background-color: ${({ active, saving, disabled }) => (saving ? "#e6e6e6" : active && !disabled ? "#00494b" : "#cccccc")};
  }
`;

const ToggleLabel = styled("label")<{ active?: boolean; saving?: boolean; disabled?: boolean }>`
  position: relative;
  display: inline-block;
  width: ${SIZES.toggleWidth};
  height: ${SIZES.toggleHeight};
  padding: ${SPACINGS.none};
  cursor: ${({ saving, disabled }) => (saving || disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ saving, disabled }) => (saving || disabled ? "none" : "auto")};
  opacity: 1;

  &:hover span:first-of-type {
    background-color: ${({ active, saving, disabled }) => {
      if (saving) return "var(--color-saveButtonDisabledIcon, #DBDAE3)";
      if (disabled) return COLORS.toggleInactive;
      if (active) return COLORS.toggleActiveHover;
      return COLORS.toggleInactiveHover;
    }};
    transition: ${({ saving, disabled }) => (saving || disabled ? "none" : "none")};
    box-shadow: ${({ active, saving, disabled }) => {
      if (saving || disabled) return "none";
      if (active) {
        return `0 0 0 3px rgba(120, 113, 246, 0.15) !important`;
      }
      return `0 0 0 3px rgba(94, 93, 132, 0.15) !important`;
    }};
    animation: none;
  }
  
  /* No idle animation: a permanent pulse on every licensed row was visual
     noise and made it hard to track which row the cursor was over. The hover
     ring above is the affordance. */
  &:not(:hover) span:first-of-type {
    animation: none;
  }

  html[data-crm="hubspot"] & {
    width: 36px;
    height: 20px;
  }

  html[data-crm="hubspot"] &:hover span:first-of-type {
    background-color: ${({ active, saving, disabled }) => (saving ? "#e6e6e6" : disabled ? "#cccccc" : active ? "#007c7d" : "#8a8a8a")};
    box-shadow: none !important;
  }
`;

const SliderThumb = styled("span")<{ active?: boolean; saving?: boolean; disabled?: boolean }>`
  content: "";
  position: absolute;
  top: ${SIZES.toggleThumbOffset};
  left: ${({ active }) =>
    active ? SIZES.toggleThumbActiveOffset : SIZES.toggleThumbOffset};
  width: ${SIZES.toggleThumbSize};
  height: ${SIZES.toggleThumbSize};
  /* The knob is always literally white — --color-backgroundWhite is the dark
     card surface in dark mode, which made the knob look black. */
  background-color: #ffffff;
  border-radius: 50%;
  z-index: 2;

  html[data-crm="hubspot"] & {
    top: 2px;
    left: ${({ active }) => (active ? "18px" : "2px")};
    width: 16px;
    height: 16px;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(20, 20, 20, 0.24);
    transition: left 150ms ease-out;
  }
  transition: ${({ disabled }) => (disabled ? "none" : "left 0.18s cubic-bezier(0.4, 0, 0.2, 1)")};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SliderIcon = styled("span")<{ active?: boolean; saving?: boolean; disabled?: boolean }>`
  content: "";
  position: absolute;
  top: ${SIZES.toggleThumbOffset};
  left: ${({ active }) =>
    active ? SIZES.toggleThumbActiveOffset : SIZES.toggleThumbOffset};
  width: ${SIZES.toggleThumbSize};
  height: ${SIZES.toggleThumbSize};
  background-size: ${SPACINGS.md} ${SPACINGS.md};
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 50%;
  z-index: 3;
  transition: ${({ saving, disabled }) => (saving || disabled ? "none" : "left 0.15s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease")};
  opacity: 1;

  /* HubSpot's knob is plain white, without the check or cross. */
  html[data-crm="hubspot"] & {
    display: none;
  }
  background-image: ${({ active, saving, disabled }) => {
    const iconColor = saving ? "#9CA3AF" : COLORS.toggleInactive;
    const checkColor = saving ? "#9CA3AF" : COLORS.toggleActive;
    
    // Encode colors for URL (replace # with %23)
    const encodedIconColor = iconColor.replace("#", "%23");
    const encodedCheckColor = checkColor.replace("#", "%23");
    
    if (active) {
      return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M20 6L9 17L4 12' stroke='${encodedCheckColor}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E")`;
    }
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M18.7071 6.70711C19.0976 6.31658 19.0976 5.68342 18.7071 5.29289C18.3166 4.90237 17.6834 4.90237 17.2929 5.29289L12 10.5858L6.70711 5.29289C6.31658 4.90237 5.68342 4.90237 5.29289 5.29289C4.90237 5.68342 4.90237 6.31658 5.29289 6.70711L10.5858 12L5.29289 17.2929C4.90237 17.6834 4.90237 18.3166 5.29289 18.7071C5.68342 19.0976 6.31658 19.0976 6.70711 18.7071L12 13.4142L17.2929 18.7071C17.6834 19.0976 18.3166 19.0976 18.7071 18.7071C19.0976 18.3166 19.0976 17.6834 18.7071 17.2929L13.4142 12L18.7071 6.70711Z' fill='${encodedIconColor}'/%3E%3C/svg%3E")`;
  }};
`;

const ToggleWrapper = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export interface ToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  saving?: boolean;
  disabled?: boolean;
  className?: string;
}

const Toggle: FC<ToggleProps> = ({
  checked = false,
  onChange,
  label,
  saving = false,
  disabled = false,
  className = "",
}) => {
  const [isActive, setIsActive] = useState(checked);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || saving) {
      return;
    }

    const newActive = !isActive;
    setIsActive(newActive);
    onChange?.(newActive);
  };

  const active = checked !== undefined ? checked : isActive;

  return (
    <ToggleWrapper className={className}>
      {label && <Text className="mr-4">{label}</Text>}
      <ToggleContainer saving={saving}>
        <ToggleLabel
          className="toggle-label"
          active={active}
          saving={saving}
          disabled={disabled}
          onClick={handleClick}
        >
          <ToggleInput type="checkbox" checked={active} readOnly />
          <Slider active={active} saving={saving} disabled={disabled} />
          <SliderThumb active={active} saving={saving} disabled={disabled} />
          <SliderIcon active={active} saving={saving} disabled={disabled} />
        </ToggleLabel>
      </ToggleContainer>
    </ToggleWrapper>
  );
};

export default Toggle;

